/* eslint-disable max-len */
import * as yup from "yup";

import { AvatarCustom, CentralLoadingIndicator, ExpandableAccordion } from "../../../components";
import { Box, Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useCancelAutoRenewalPreference, useGetAutoRenewalPreference, useGetAutoRenewalUserPreferences } from "../../../services/renewal-preference/renewal-preference.service";
import useWellnessPlans, { CategoryItem } from "../../../hooks/useWellnessPlans";

import { CloseIcon } from "../../../components/Icons";
import ExpandDownIcon from "../../../components/Icons/ExpandDownIcon";
import ExpandUpIcon from "../../../components/Icons/ExpandUpIcon";
import { Listbox } from "../../../styles/global.style";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { MODALS } from "../../../config/params";
import WellnessBloodworkIcon from "../../../components/Icons/WellnessBloodworkIcon";
import WellnessDiagnosticsIcon from "../../../components/Icons/WellnessDiagnosticsIcon";
import WellnessExamsIcon from "../../../components/Icons/WellnessExamsIcon";
import WellnessVaccinationIcon from "../../../components/Icons/WellnessVaccinationIcon";
import { toastMessageActions } from "../../../state/request";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useGetAlerts } from "../../../services/get-alerts/get-alerts.service";
import { useModal } from "../../../context/ModalContext";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../../../utils/mixpanel";
import { yupResolver } from "@hookform/resolvers/yup";

const REASON_FOR_CANCEL_RENEWAL = [
  { label: "Move hospitals (outside of GoodVets)", value: "Move hospitals (outside of GoodVets)" },
  { label: "Not satisfied with service", value: "Not satisfied with service" },
  { label: "I changed my mind", value: "I changed my mind" },
];

interface IBenefitCategory {
  name: string;
  items: CategoryItem[];
}

const CancelRenewalSchema = yup.object({
  reason: yup.string().required("Please select a reason for cancelling"),
});

// TODO(wellness-renewal): update cancel-preference confirm + success modal copy once design copy is provided
const CancelUpcomingPlanRenewalModal = () => {
  const { closeModal, openModal, params } = useModal();
  const subscriptionId = params?.subscriptionId as string;
  const petId = params?.petId as string;
  const { cancelRenewalPreference, response, loading } = useCancelAutoRenewalPreference();
  const { preference, loading: preferenceLoading } = useGetAutoRenewalPreference(subscriptionId);
  const { petList } = usePetStore();
  const {
    fetchCatalogItems,
    loading: catalogLoading,
    error: catalogError,
    getPlanDataByPlanId,
    annualSegregatedPlanData,
    monthlySegregatedPlanData,
  } = useWellnessPlans();
  const { refetch: refetchUpcomingPlans } = useGetAutoRenewalUserPreferences();
  const { refetch: refetchAlerts } = useGetAlerts();

  const [openReasonsList, setOpenReasonsList] = useState(false);
  const [reasonSelected, setReasonSelected] = useState<string>("");
  const [benefits, setBenefits] = useState<IBenefitCategory[]>([]);

  const dispatch = useDispatch();

  const petDetails = petList?.find((pet) => pet.id === petId);

  const { handleSubmit, setValue, getValues } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(CancelRenewalSchema),
    defaultValues: {
      "reasons": "",
    },
  });

  const handleChange = (value: any) => {
    setReasonSelected(value);
    setValue("reasons", value);
  };

  const handleClose = () => {
    if (petDetails && preference) {
      userActivityLogger(MIXPANEL_EVENTS.USER_CLOSES_CANCEL_RENEWAL,
        {
          pet_name: petDetails.name,
          wellness_plan_name: preference.newPlanName,
        });
    }
    closeModal();
  };

  const handleCancelRenewalSubmit = () => {
    if (subscriptionId && petDetails && preference) {
      userActivityLogger(MIXPANEL_EVENTS.USER_CANCEL_RENEWAL_TRIGGERED,
        {
          pet_name: petDetails.name,
          selected_reasons: reasonSelected,
          wellness_plan_name: preference.newPlanName,
        });
      cancelRenewalPreference(subscriptionId);
    }
  };

  useEffect(() => {
    if (petId) {
      fetchCatalogItems(petId);
    }
  }, [petId]);

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.CANCEL_UPCOMING_PLAN_RENEWAL_MODAL_SHOWN, {
      subscriptionId: subscriptionId,
      petId: petId,
      petName: petDetails?.name,
      planName: preference?.newPlanName,
      planType: preference?.newPlanType,
      planStartDate: preference?.newPlanStartDate,
    });
  }, []);

  useEffect(() => {
    const hasAnnualData = annualSegregatedPlanData && Object.keys(annualSegregatedPlanData).length > 0;
    const hasMonthlyData = monthlySegregatedPlanData && Object.keys(monthlySegregatedPlanData).length > 0;

    if (preference?.newPlanId && (hasAnnualData || hasMonthlyData)) {
      const planData = getPlanDataByPlanId(preference.newPlanId, preference.newPlanType);

      if (planData?.services) {
        // Reorder to put "Discounts" last
        const entries = Object.entries(planData.services);
        const sorted = entries.sort(([a], [b]) => {
          if (a.toLowerCase() === "discounts") return 1;
          if (b.toLowerCase() === "discounts") return -1;

          return 0;
        });

        const benefitsArray: IBenefitCategory[] = sorted.map(
          ([categoryName, items]) => ({
            name: categoryName,
            items: items,
          }),
        );

        setBenefits(benefitsArray);
      }
    }
  }, [preference, annualSegregatedPlanData, monthlySegregatedPlanData]);

  useEffect(() => {
    if (response && petDetails && preference) {
      if (response?.success) {
        userActivityLogger(MIXPANEL_EVENTS.RENEWAL_CANCELLATION_SUCCESS,
          {
            pet_name: petDetails.name,
            selected_reasons: reasonSelected,
            wellness_plan_name: preference.newPlanName,
          });
        dispatch(toastMessageActions.setGlobalAlert({ severity: "success", message: response.messages[0] }));
        openModal(MODALS.CANCEL_UPCOMING_PLAN_RENEWAL_SUCCESS, "plan_and_billing", {
          subscriptionId,
          petId,
        });
        refetchUpcomingPlans();
        refetchAlerts();
      } else {
        userActivityLogger(MIXPANEL_EVENTS.RENEWAL_CANCELLATION_FAILURE,
          {
            pet_name: petDetails.name,
            selected_reasons: reasonSelected,
            wellness_plan_name: preference.newPlanName,
            error_message: response.messages[0],
          });
      }
    }
  }, [response]);

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();

    if (name.includes("vaccine")) return <WellnessVaccinationIcon />;
    if (name.includes("diagnostic")) return <WellnessDiagnosticsIcon />;
    if (name.includes("bloodwork")) return <WellnessBloodworkIcon />;
    if (name.includes("discount")) return null;

    return <WellnessExamsIcon />;
  };

  if (preferenceLoading || catalogLoading) {
    return <CentralLoadingIndicator />;
  }

  return (
    <form onSubmit={handleSubmit(handleCancelRenewalSubmit)} className="relative h-[95%]">
      <div className="flex justify-end mb-4">
        <CloseIcon
          onClick={() => !loading && handleClose()}
          className={`${loading ? "text-gray-3" : "text-light-black"} cursor-pointer mt-1`}
        />
      </div>
      <Box className="h-[80%] overflow-y-auto flex flex-col gap-4 flex-1 basis-0 p-6 pe-4 pt-2">

        <div className="flex flex-col gap-1 items-center">
          <h6 className="text-base font-semibold">Cancel Plan
            Renewal for <span className="capitalize">{petDetails?.name}</span>?</h6>
          <h6 className="text-sm">You will lose all the following benefits</h6>
        </div>

        {/* Pet PLAN CARD*/}
        <div className="w-full py-3 pl-4 rounded-lg flex items-center gap-3 border border-taupe-50 bg-white">
          <AvatarCustom
            image={petDetails?.image}
            name={petDetails?.name}
            width={80}
            height={80}
            isWellnessMember={true}
          />

          <div className="flex flex-col gap-2 items-start ">
            <h4 className="font-semibold text-xl">{preference?.newPlanName}</h4>
            <h6 className="capitalize">{petDetails?.name}</h6>
          </div>

        </div>

        {/* BENEFITS LIST */}
        <div className="flex flex-col gap-2 w-full">
          {benefits.length > 0 ? (
            benefits.map((category, index) => {
              const hasItems = category.items && category.items.length > 0;

              if (!hasItems) return null;

              const description = (
                <ul className="!list-disc">
                  {category.items.map((item, idx) => (
                    <li key={idx}>
                      {category.name.toLowerCase().includes("discount") ? (
                        <>
                          <span className="font-semibold me-1">
                            {item.discount}% Off
                          </span>
                          <span>{item.name}</span>
                        </>
                      ) : (
                        <>
                          {item.quantity > 0 && item.quantity !== -1 && (
                            <span className="me-1">{item.quantity}</span>
                          )}
                          <span>{item.name}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              );

              const icon = getCategoryIcon(category.name);

              return (
                <ExpandableAccordion
                  key={index}
                  removeBorder={false}
                  onExpand={() => {}}
                  icon={icon}
                  title={category.name}
                  description={description}
                />
              );
            })
          ) : (
            <div className="text-sm text-gray-1 text-center p-4">
              {catalogError ?
                "Unable to load plan benefits" :
                "Loading plan benefits..."}
            </div>
          )}
        </div>

        {/* REASONS */}

        <div className="flex flex-col gap-2 w-full">
          <h6 className="text-sm font-semibold">Select a Reason for Cancelling</h6>
          <div className={`flex justify-between bg-white rounded-lg border 
          ${openReasonsList ? "border-gv-yellow" : "border-taupe-50" }
          py-2 pr-2 pl-3 items-center self-stretch cursor-pointer`} onClick={() => setOpenReasonsList(!openReasonsList)}>

            {getValues("reasons") ? <p className="text-sm">{getValues("reasons")}</p> :
              <p className="text-gray-1 text-sm">Select a Reason</p>}

            <div>
              {openReasonsList ?
                <ExpandUpIcon width={20} height={20} /> : <ExpandDownIcon width={20} height={20} />}
            </div>

          </div>
          {openReasonsList && (
            <div className="relative -top-2">
              <Listbox>
                {REASON_FOR_CANCEL_RENEWAL.map((reasonsList, index) => (
                  <li key={index} onClick={() => {
                    handleChange(reasonsList.value);
                    setOpenReasonsList(false);
                  }}>
                    {reasonsList.label}
                  </li>
                ))}
              </Listbox>
            </div>
          )}
        </div>

      </Box>
      <Box className="!sticky !bottom-0 !bg-gray-2 w-full pt-8">

        <Button variant="outlined" type="submit" onClick={handleCancelRenewalSubmit}
          disabled={!getValues("reasons")}
          className="!normal-case !font-semibold !border !border-taupe-2 !bg-gray-3 !w-full">
          {loading ? <CircularProgress color="inherit" size={20} /> :
            "Cancel Renewal"}
        </Button>
      </Box>
    </form>
  );
};

export default CancelUpcomingPlanRenewalModal;

